import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Copy, Download, FileText, Link as LinkIcon, Plus, Trash2 } from "lucide-react";
import { listarViajeros, guardarViajero, eliminarViajero } from "@/lib/viajeros.functions";
import { emitirFactura, obtenerFactura, descargarFacturaPDF } from "@/lib/facturas.functions";

export const Route = createFileRoute("/_authenticated/gestion/reservas/$id")({
  component: FichaReserva,
});

type Reserva = {
  id: string;
  nombre_huesped: string;
  email_huesped: string;
  telefono_huesped: string;
  fecha_entrada: string;
  fecha_salida: string;
  num_huespedes: number;
  precio_total: number;
  estado: string;
  habitacion_id: string;
  checkin_token: string | null;
  checkin_completado_at: string | null;
  habitacion?: { nombre: string } | null;
};

type Viajero = {
  id: string;
  rol: "titular" | "viajero";
  nombre: string;
  apellido1: string;
  apellido2: string | null;
  sexo: "H" | "M" | "O" | null;
  fecha_nacimiento: string;
  nacionalidad: string;
  tipo_documento: "DNI" | "NIE" | "PAS" | "OTRO";
  numero_documento: string;
  fecha_expedicion: string | null;
  direccion: string | null;
  ciudad: string | null;
  codigo_postal: string | null;
  pais_residencia: string | null;
  telefono: string | null;
  email: string | null;
  parentesco: string | null;
};

const emptyViajero = (): Omit<Viajero, "id"> => ({
  rol: "viajero",
  nombre: "",
  apellido1: "",
  apellido2: "",
  sexo: null,
  fecha_nacimiento: "",
  nacionalidad: "ESP",
  tipo_documento: "DNI",
  numero_documento: "",
  fecha_expedicion: null,
  direccion: "",
  ciudad: "",
  codigo_postal: "",
  pais_residencia: "ESP",
  telefono: "",
  email: "",
  parentesco: null,
});

function FichaReserva() {
  const { id } = Route.useParams();
  const listar = useServerFn(listarViajeros);
  const guardar = useServerFn(guardarViajero);
  const eliminar = useServerFn(eliminarViajero);
  const emitir = useServerFn(emitirFactura);
  const obtener = useServerFn(obtenerFactura);
  const descargar = useServerFn(descargarFacturaPDF);

  const [reserva, setReserva] = useState<Reserva | null>(null);
  const [viajeros, setViajeros] = useState<Viajero[]>([]);
  const [factura, setFactura] = useState<{ numero_completo: string | null; fecha_emision: string; total: number } | null>(null);
  const [nuevo, setNuevo] = useState<Omit<Viajero, "id"> | null>(null);
  const [facturaError, setFacturaError] = useState<string | null>(null);
  const [emitiendoFactura, setEmitiendoFactura] = useState(false);

  const load = useCallback(async () => {
    const { data: r } = await supabase
      .from("reservas")
      .select("*, habitacion:habitaciones(nombre)")
      .eq("id", id)
      .single();
    setReserva(r as Reserva);
    const vs = await listar({ data: { reserva_id: id } });
    setViajeros(vs as Viajero[]);
    const f = await obtener({ data: { reserva_id: id } });
    setFactura(f as never);
  }, [id, listar, obtener]);

  useEffect(() => { load(); }, [load]);

  async function handleSaveViajero(v: Omit<Viajero, "id"> & { id?: string }) {
    try {
      await guardar({ data: { ...v, reserva_id: id } });
      setNuevo(null);
      load();
      toast.success("Viajero guardado");
    } catch (e) { toast.error((e as Error).message); }
  }

  async function handleEmitir() {
    try {
      setFacturaError(null);
      setEmitiendoFactura(true);
      const f = await emitir({ data: { reserva_id: id } });
      toast.success(`Factura ${(f as { numero_completo?: string }).numero_completo ?? ""} emitida`);
      load();
    } catch (e) {
      const message = e instanceof Error ? e.message : "No se pudo emitir la factura";
      setFacturaError(message);
      toast.error(message);
    } finally {
      setEmitiendoFactura(false);
    }
  }

  async function handleDescargar() {
    try {
      const { filename, base64 } = await descargar({ data: { reserva_id: id } });
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error((e as Error).message); }
  }

  if (!reserva) return <div className="text-muted-foreground">Cargando…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="icon"><Link to="/gestion/reservas"><ArrowLeft className="h-4 w-4" /></Link></Button>
        <div>
          <h1 className="font-serif text-2xl">{reserva.nombre_huesped}</h1>
          <p className="text-sm text-muted-foreground">
            {reserva.habitacion?.nombre} · {reserva.fecha_entrada} → {reserva.fecha_salida} · {reserva.num_huespedes} pax · {Number(reserva.precio_total).toFixed(2)} €
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Registro de viajeros (SES.Hospedajes)</CardTitle>
          <Button size="sm" onClick={() => setNuevo(emptyViajero())}><Plus className="mr-1 h-4 w-4" />Añadir viajero</Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {viajeros.length === 0 && !nuevo && (
            <p className="text-sm text-muted-foreground">Aún no has registrado ningún viajero para esta reserva.</p>
          )}
          {viajeros.map((v) => (
            <div key={v.id} className="flex items-center justify-between rounded-md border bg-card p-3 text-sm">
              <div>
                <div className="font-medium">{v.nombre} {v.apellido1} {v.apellido2 ?? ""}</div>
                <div className="text-xs text-muted-foreground">
                  {v.tipo_documento} {v.numero_documento} · {v.nacionalidad} · nac. {v.fecha_nacimiento}
                </div>
              </div>
              <Button size="icon" variant="ghost" onClick={async () => {
                if (!confirm("¿Eliminar viajero?")) return;
                await eliminar({ data: { id: v.id } });
                load();
              }}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          {nuevo && (
            <ViajeroForm value={nuevo} onCancel={() => setNuevo(null)} onSave={handleSaveViajero} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><LinkIcon className="h-5 w-5" />Check-in online</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {reserva.checkin_token ? (
            <CheckinLink token={reserva.checkin_token} completado={reserva.checkin_completado_at} />
          ) : (
            <p className="text-sm text-muted-foreground">Esta reserva aún no tiene enlace de check-in.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5" />Factura</CardTitle></CardHeader>
        <CardContent>
          {factura ? (
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <div className="font-medium">Factura nº {factura.numero_completo}</div>
                <div className="text-xs text-muted-foreground">Emitida el {factura.fecha_emision} · {Number(factura.total).toFixed(2)} €</div>
              </div>
              <Button onClick={handleDescargar}><Download className="mr-1 h-4 w-4" />Descargar PDF</Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Todavía no se ha emitido factura para esta reserva.</p>
                {facturaError && <p className="mt-2 text-sm text-destructive">{facturaError}</p>}
              </div>
              <Button onClick={handleEmitir} disabled={emitiendoFactura}>
                {emitiendoFactura ? "Emitiendo…" : "Emitir factura"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ViajeroForm({ value, onCancel, onSave }: {
  value: Omit<Viajero, "id"> & { id?: string };
  onCancel: () => void;
  onSave: (v: Omit<Viajero, "id"> & { id?: string }) => void;
}) {
  const [v, setV] = useState(value);
  const set = <K extends keyof typeof v>(k: K, val: (typeof v)[K]) => setV((p) => ({ ...p, [k]: val }));
  return (
    <div className="rounded-md border bg-secondary/30 p-4 space-y-3">
      <div className="grid gap-3 md:grid-cols-3">
        <div><Label>Rol</Label>
          <Select value={v.rol} onValueChange={(x) => set("rol", x as "titular" | "viajero")}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="titular">Titular</SelectItem>
              <SelectItem value="viajero">Viajero</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Nombre *</Label><Input value={v.nombre} onChange={(e) => set("nombre", e.target.value)} /></div>
        <div><Label>Sexo</Label>
          <Select value={v.sexo ?? ""} onValueChange={(x) => set("sexo", (x || null) as "H" | "M" | "O" | null)}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="H">Hombre</SelectItem>
              <SelectItem value="M">Mujer</SelectItem>
              <SelectItem value="O">Otro</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Primer apellido *</Label><Input value={v.apellido1} onChange={(e) => set("apellido1", e.target.value)} /></div>
        <div><Label>Segundo apellido</Label><Input value={v.apellido2 ?? ""} onChange={(e) => set("apellido2", e.target.value)} /></div>
        <div><Label>Fecha nacimiento *</Label><Input type="date" value={v.fecha_nacimiento} onChange={(e) => set("fecha_nacimiento", e.target.value)} /></div>
        <div><Label>Nacionalidad (ISO α-3) *</Label><Input value={v.nacionalidad} onChange={(e) => set("nacionalidad", e.target.value.toUpperCase())} maxLength={3} /></div>
        <div><Label>Tipo documento *</Label>
          <Select value={v.tipo_documento} onValueChange={(x) => set("tipo_documento", x as "DNI" | "NIE" | "PAS" | "OTRO")}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="DNI">DNI</SelectItem>
              <SelectItem value="NIE">NIE</SelectItem>
              <SelectItem value="PAS">Pasaporte</SelectItem>
              <SelectItem value="OTRO">Otro</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Nº documento *</Label><Input value={v.numero_documento} onChange={(e) => set("numero_documento", e.target.value)} /></div>
        <div><Label>Fecha expedición</Label><Input type="date" value={v.fecha_expedicion ?? ""} onChange={(e) => set("fecha_expedicion", e.target.value || null)} /></div>
        <div className="md:col-span-2"><Label>Dirección</Label><Input value={v.direccion ?? ""} onChange={(e) => set("direccion", e.target.value)} /></div>
        <div><Label>Ciudad</Label><Input value={v.ciudad ?? ""} onChange={(e) => set("ciudad", e.target.value)} /></div>
        <div><Label>Código postal</Label><Input value={v.codigo_postal ?? ""} onChange={(e) => set("codigo_postal", e.target.value)} /></div>
        <div><Label>País residencia (ISO α-3)</Label><Input value={v.pais_residencia ?? ""} onChange={(e) => set("pais_residencia", e.target.value.toUpperCase())} maxLength={3} /></div>
        <div><Label>Teléfono</Label><Input value={v.telefono ?? ""} onChange={(e) => set("telefono", e.target.value)} /></div>
        <div><Label>Email</Label><Input value={v.email ?? ""} onChange={(e) => set("email", e.target.value)} /></div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>Cancelar</Button>
        <Button onClick={() => onSave(v)}>Guardar</Button>
      </div>
    </div>
  );
}

function CheckinLink({ token, completado }: { token: string; completado: string | null }) {
  const url = `https://elpajardebenito.es/checkin/${token}`;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input readOnly value={url} className="font-mono text-xs" />
        <Button size="sm" variant="outline" onClick={() => {
          navigator.clipboard.writeText(url);
          toast.success("Enlace copiado");
        }}><Copy className="mr-1 h-4 w-4" />Copiar</Button>
        <Button asChild size="sm" variant="outline"><a href={url} target="_blank" rel="noreferrer">Abrir</a></Button>
      </div>
      {completado ? (
        <p className="text-xs text-emerald-600">Check-in completado el {new Date(completado).toLocaleString("es-ES")}.</p>
      ) : (
        <p className="text-xs text-muted-foreground">Comparte este enlace con el huésped para que registre a los viajeros antes de llegar.</p>
      )}
    </div>
  );
}